/**
 * **What this till reaches for** — the recently rung items, newest first (B4).
 *
 * The ask was *"almost just like a calculator, but with my Eminence products in
 * there"* (`docs/lovespa-discovery.md` §3). A calculator has no menu: the thing
 * you want is under your thumb. A spa with twenty-one treatments and forty
 * products has a picker, and the difference between a picker and a keypad is
 * almost entirely **which eight things are on top**.
 *
 * ## Why the device decides this and not the server
 *
 * `topSellingProducts` exists in `packages/store` and would answer a *better*
 * question — what this business sells most — with a round trip, a report window
 * and a cache. This answers a worse question much faster: what *this counter*
 * rang in the last while. Three things make the worse question the right one
 * here.
 *
 *  1. **It has to work in a basement.** The whole staff app is offline-first
 *     (spec §8); an ordering that arrives over the network is an ordering that
 *     vanishes exactly when the counter is busiest.
 *  2. **It is a preference, not a fact.** Nothing is decided by it, no money
 *     moves because of it, and it is wrong in no way that matters — a stale
 *     order costs one scroll. `localStorage` is the honest home for a value with
 *     those properties, and the app's own convention is that anything that
 *     *matters* goes in the mirror's IndexedDB with the rest of the book.
 *  3. **It is per device on purpose.** The iPad at the counter and the phone in
 *     her pocket are used for different things, and averaging them would make
 *     both slightly wrong.
 *
 * ## Storage, and what happens when it is not there
 *
 * One key per tenant (`ys.till.recents.<tenant>`), a JSON array of item ids, cap
 * `RECENTS_MAX`. Every read and every write is wrapped: Safari in private mode
 * throws on `localStorage` access rather than returning null, and a till that
 * would not open because it could not remember an ordering would be a spectacular
 * way to fail. **Absent, unreadable and corrupt all mean the same thing** — no
 * recents, the catalog in its own order, which is exactly the screen that shipped
 * before this file existed.
 */

/** How many ids to keep. Two rows of four on the narrowest till we support. */
export const RECENTS_MAX = 8;

function keyFor(tenantId: string): string {
  return `ys.till.recents.${tenantId}`;
}

/**
 * The store, or `null` when there is not one.
 *
 * `globalThis.localStorage` rather than a bare `localStorage` so this module is
 * importable from a test environment that has no DOM, and the property access
 * itself is inside the `try` because *reading the property* is what throws in a
 * locked-down browser.
 */
function storage(): Storage | null {
  try {
    return (globalThis.localStorage as Storage | undefined) ?? null;
  } catch {
    return null;
  }
}

/** The ids this device rang most recently, newest first. Never throws. */
export function readRecents(tenantId: string): string[] {
  const store = storage();
  if (store === null || tenantId === '') return [];
  try {
    const raw = store.getItem(keyFor(tenantId));
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((id): id is string => typeof id === 'string').slice(0, RECENTS_MAX);
  } catch {
    return [];
  }
}

/**
 * Remember that these items were just sold, newest first.
 *
 * Takes the whole sale rather than one id at a time, because a sale is what
 * happens: ringing three things and remembering only the last would make the
 * ordering lag the counter by two taps. The ids are pushed in **reverse** so the
 * first line of the sale ends up first in the list, which is the order the owner
 * added them in and therefore the order she thinks in.
 */
export function rememberRecents(tenantId: string, itemIds: readonly string[]): void {
  const store = storage();
  if (store === null || tenantId === '' || itemIds.length === 0) return;
  const next = [...readRecents(tenantId)];
  for (const id of [...itemIds].reverse()) {
    const at = next.indexOf(id);
    if (at >= 0) next.splice(at, 1);
    next.unshift(id);
  }
  try {
    store.setItem(keyFor(tenantId), JSON.stringify(next.slice(0, RECENTS_MAX)));
  } catch {
    // A full or locked-down store is not a failed checkout. The sale already
    // happened; this is the part that only decides what is convenient next time.
  }
}

/**
 * Order `items` so the recently-rung ones come first, then everything else in
 * the order it arrived (which is the catalog's own).
 *
 * Pure, so the ordering can be tested without a browser, and stable, so two
 * items nobody has sold keep their catalog order rather than shuffling on every
 * render.
 */
export function byRecency<T extends { id: string }>(
  items: readonly T[],
  recents: readonly string[],
): T[] {
  const rank = new Map(recents.map((id, index) => [id, index]));
  return [...items].sort((a, b) => {
    const left = rank.get(a.id) ?? Number.MAX_SAFE_INTEGER;
    const right = rank.get(b.id) ?? Number.MAX_SAFE_INTEGER;
    return left === right ? 0 : left - right;
  });
}
